import { useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Card, CardContent, CardTitle, CardDescription, CardHeader } from '@g17eco/ui-modern/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@g17eco/ui-modern/components/ui/chart';
import { useGetFacilitiesComparisonQuery } from '../api/emissions';
import type { FacilityEmissionData } from '../types/emissions';
import { useEmissionsFormatter } from '../hooks/useEmissionsFormatter';
import { UNIT_LABELS } from '../utils/emissionsFormatter';
import { SCOPE_COLORS } from '../config/scopeBranding';
import { Progress } from '@g17eco/ui-modern/components/ui/progress';
import { useAppSelector } from '@reducers/index';
import { getCurrentInitiativeTree } from '@selectors/initiative';

interface FacilitiesComparisonPanelProps {
  initiativeId: string;
  periodStart?: string;
  periodEnd?: string;
  includeChildren?: boolean;
}

const SCOPE_LABELS: Record<string, string> = {
  scope1: 'Scope 1',
  scope2: 'Scope 2',
  scope3: 'Scope 3',
};

export const FacilitiesComparisonPanel = ({
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
}: FacilitiesComparisonPanelProps) => {
  const { formatCompact, settings } = useEmissionsFormatter();

  const defaultStart = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 11);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  }, []);

  const defaultEnd = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  }, []);

  const { data, isLoading, error } = useGetFacilitiesComparisonQuery(
    {
      initiativeId,
      periodStart: periodStart || defaultStart,
      periodEnd: periodEnd || defaultEnd,
      includeChildren,
    },
    { skip: !initiativeId }
  );

  // All child reporting levels from the initiative tree (excludes current initiative at level 0)
  const allChildren = useAppSelector(getCurrentInitiativeTree);

  // Merge: show ALL child reporting levels, filling zero-emission placeholders for those missing from comparison data
  const facilities = useMemo((): FacilityEmissionData[] => {
    const apiFacilities = data?.facilities ?? [];
    if (allChildren.length === 0) return apiFacilities;
    const children = allChildren;

    const apiMap = new Map(apiFacilities.map((f) => [f._id, f]));
    const merged: FacilityEmissionData[] = children.map((child) => {
      const existing = apiMap.get(child._id);
      if (existing) return existing;
      return { _id: child._id, name: child.name, code: child.code, scopes: [], totalCo2e: 0, totalActivities: 0 };
    });

    // Sort: emitting first (desc), then zero alphabetically
    merged.sort((a, b) => {
      if (a.totalCo2e > 0 && b.totalCo2e === 0) return -1;
      if (a.totalCo2e === 0 && b.totalCo2e > 0) return 1;
      if (a.totalCo2e > 0 && b.totalCo2e > 0) return b.totalCo2e - a.totalCo2e;
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    return merged;
  }, [data?.facilities, allChildren]);

  const scopes = useMemo(() => {
    const scopeSet = new Set<string>();
    facilities.forEach((facility) => {
      facility.scopes.forEach((s) => scopeSet.add(s.scope));
    });
    return ['scope1', 'scope2', 'scope3'].filter((s) => scopeSet.has(s));
  }, [facilities]);

  const getScopeValueInDisplayUnit = useCallback(
    (facility: FacilityEmissionData, scope: string): number => {
      const scopeData = facility.scopes.find((s) => s.scope === scope);
      if (!scopeData) {
        return 0;
      }
      return formatCompact(scopeData.co2e).value;
    },
    [formatCompact]
  );

  const rechartsData = useMemo(() => {
    return facilities.map((facility) => {
      const row: Record<string, string | number> = { name: facility.name || 'Unknown' };
      scopes.forEach((scope) => {
        row[scope] = getScopeValueInDisplayUnit(facility, scope);
      });
      return row;
    });
  }, [facilities, scopes, getScopeValueInDisplayUnit]);

  const barChartConfig = useMemo((): ChartConfig => {
    const config: ChartConfig = {};
    scopes.forEach((scope) => {
      config[scope] = { label: SCOPE_LABELS[scope] || scope, color: SCOPE_COLORS[scope] || '#6c757d' };
    });
    return config;
  }, [scopes]);

  const axisUnitLabel = UNIT_LABELS[settings.displayUnit] || 'tCO₂e';

  const hasData = facilities.length > 0;

  if (isLoading) {
    return (
      <div className='tw:flex tw:items-center tw:justify-center tw:py-10'>
        <Loader2 className='tw:size-6 tw:animate-spin tw:text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className='tw:p-4 tw:text-center'>
          <i className='fa fa-exclamation-triangle fa-2x tw:text-red-500 tw:mb-2 tw:opacity-50' />
          <p className='tw:text-muted-foreground tw:mb-0'>Failed to load reporting level comparison data</p>
        </CardContent>
      </Card>
    );
  }

  if (!hasData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Reporting Level Comparison</CardTitle>
          <CardDescription>Emissions breakdown by reporting level and scope</CardDescription>
        </CardHeader>
        <CardContent>
          <div className='tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-muted-foreground tw:h-[280px]'>
            <i className='fa fa-building fa-3x tw:mb-3 tw:opacity-50' />
            <h5 className='tw:mb-2 tw:font-semibold'>No Reporting Level Data</h5>
            <p className='tw:text-center tw:mb-3 tw:text-sm tw:max-w-md'>
              No emission data found for subsidiary reporting levels. Add activities to subsidiaries to see
              their emissions comparison here.
            </p>
            <p className='tw:text-center tw:text-muted-foreground tw:mb-0 tw:text-sm tw:max-w-md'>
              Currently comparing all subsidiaries. For asset-level comparison, see the Assets tab.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalCo2e = facilities.reduce((sum, f) => sum + f.totalCo2e, 0);
  const totalFormatted = formatCompact(totalCo2e);
  const chartHeight = Math.max(280, facilities.length * 50);

  return (
    <div className='tw:grid tw:grid-cols-1 tw:lg:grid-cols-12 tw:gap-4'>
      {/* Stacked Bar Chart */}
      <div className='tw:lg:col-span-7'>
        <Card className='tw:h-full'>
          <CardHeader>
            <CardTitle>Reporting Level Comparison</CardTitle>
            <CardDescription>Emissions breakdown by reporting level ({facilities.length} reporting levels)</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={barChartConfig} className='tw:w-full' style={{ height: chartHeight }}>
              <BarChart layout='vertical' data={rechartsData} margin={{ top: 5, right: 0, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray='3 3' horizontal={false} />
                <XAxis
                  type='number'
                  label={{ value: axisUnitLabel, position: 'insideBottom', offset: -10, fontSize: 11 }}
                />
                <YAxis type='category' dataKey='name' width={110} tick={{ fontSize: 11 }} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend content={<ChartLegendContent />} />
                {scopes.map((scope) => (
                  <Bar
                    key={scope}
                    dataKey={scope}
                    name={SCOPE_LABELS[scope]}
                    fill={SCOPE_COLORS[scope] || '#6c757d'}
                    stackId='a'
                  />
                ))}
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>

      {/* Reporting Level Details Table */}
      <div className='tw:lg:col-span-5'>
        <Card className='tw:h-full'>
          <CardHeader>
            <CardTitle>Reporting Level Details</CardTitle>
            <CardDescription>Total: {totalFormatted.display}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='tw:pr-2' style={{ height: Math.max(400, facilities.length * 50), overflowY: 'auto' }}>
              {facilities.map((facility, index) => {
                const facilityFormatted = formatCompact(facility.totalCo2e);
                const percentage = totalCo2e > 0
                  ? ((facility.totalCo2e / totalCo2e) * 100).toFixed(1)
                  : '0.0';

                return (
                  <div
                    key={facility._id}
                    className={`tw:py-3 ${index < facilities.length - 1 ? 'tw:border-b tw:border-ThemeBorderDefault' : ''}`}
                  >
                    <div className='tw:flex tw:justify-between tw:items-start tw:mb-2'>
                      <div className='tw:min-w-0 tw:flex-1 tw:mr-2'>
                        <span className='tw:font-medium tw:block tw:truncate'>{facility.name || 'Unknown'}</span>
                        {facility.code && (
                          <span className='text-ThemeTextLight tw:block tw:truncate'>{facility.code}</span>
                        )}
                      </div>
                      <div className='tw:text-right'>
                        <span className='tw:font-bold'>{facilityFormatted.display}</span>
                        <span className='text-ThemeTextLight tw:block'>{percentage}%</span>
                      </div>
                    </div>
                    <div className='tw:flex tw:gap-2 tw:flex-wrap'>
                      {facility.scopes.map((scope) => {
                        const scopeFormatted = formatCompact(scope.co2e);
                        return (
                          <div key={scope.scope} className='tw:flex tw:items-center tw:gap-1'>
                            <span
                              className='tw:rounded'
                              style={{
                                width: 8,
                                height: 8,
                                backgroundColor: SCOPE_COLORS[scope.scope] || '#6c757d',
                              }}
                            />
                            <span className='text-ThemeTextLight'>
                              {SCOPE_LABELS[scope.scope] || scope.scope}:{' '}
                              {scopeFormatted.formatted} {scopeFormatted.unitCompact}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <Progress value={Number(percentage)} className='tw:mt-2 tw:h-2' indicatorClassName='tw:bg-emerald-900/80' />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
